/**
 * DCA Management P0 — controllable-lever vs state-quantity classification
 * + artificial-lift-specific lever templates.
 *
 * WHY: the legacy panel treated *every* non-time production column as a tunable
 * "parameter" and let find_optimal_x push each to its extreme. That is the §2
 *硬伤 #2/#3: "把含水设到最大来增油" / 气举注气推到底. P0 (§6) splits variables
 * into 可控杠杆 (operator can set) vs 状态/结果量 (read-only diagnostics) and
 * loads a lift-specific lever set per well's artificial_lift_type (§5.1).
 *
 * BE CONTRACT (parallel P0 backend agent). The available-params / validation
 * responses MAY carry any of the following. ALL are optional — when absent we
 * fall back to the static maps below so the panel still renders (graceful
 * degradation, §5.2 requirement):
 *
 *   {
 *     artificial_lift_type: "esp" | "gas_lift" | "rod_pump" | "natural_flow" |
 *                           "pcp" | "plunger" | "jet_pump" | <raw label>,
 *     controllable_levers: ["esp_frequency", ...]  // param keys, OR rich specs
 *                          [{col, key, unit, shape, limit, note}, ...] (current BE)
 *     lift_template: {
 *       lift_type: "esp",
 *       optimization_shape: "windowed" | "monotonic" | "humped" | "cyclic",
 *       levers: [
 *         { key, name, unit, min, max, hint }   // equipment limits + constraint
 *       ],
 *       diagnostics: ["ESP pump curve", "intake pressure"]
 *     }
 *   }
 *
 * Anything the BE omits is filled from LIFT_TEMPLATES / lever metadata here.
 */

// ---------------------------------------------------------------------------
// 1. Variable classification — controllable levers vs state/result quantities.
//    Param keys mirror the production-column keys used across dca/components.
// ---------------------------------------------------------------------------

// Canonical set of operator-controllable lever keys (union over all lift types).
// A param is treated as a lever ONLY if it is in here (or the BE explicitly
// flags it via controllable_levers). Everything else => read-only state量.
export const CONTROLLABLE_LEVER_KEYS = new Set([
  "choke_size", // choke /64 in (natural flow, ESP)
  "esp_frequency", // ESP pump frequency (Hz)
  "gas_inj_rate", // gas-lift injection rate (Mscf/d) — humped optimum
  "gas_inj_pressure", // gas-lift injection pressure (psi)
  "water_inj_rate", // water/CO2 injection rate
  "co2_inj_rate",
  "target_thp", // target wellhead / tubing head pressure setpoint
  "wellhead_back_pressure",
  "spm", // rod pump strokes per minute
  "stroke_length", // rod pump stroke length (in)
  "pump_rpm", // PCP rotational speed (RPM)
  "cycle_time", // plunger lift on/off cycle (min)
  "power_fluid_rate", // jet pump power-fluid rate
]);

// State / result quantities — NEVER optimizable. Shown read-only as 诊断.
// (含水/GOR/BHFP/WHP/温度… are *outcomes*, not knobs — §3.A / 硬伤#2.)
export const STATE_QUANTITY_KEYS = new Set([
  "bottomhole_flow_pressure",
  "wellhead_tubing_pressure",
  "wellhead_casing_pressure",
  "average_wellhead_pressure",
  "water_cut",
  "gor",
  "gas_oil_ratio",
  "temperature",
  "fluid_level",
  "dayson",
]);

// Production rate phases — never treated as a lever (they ARE the target).
export const RATE_KEYS = new Set([
  "oil_prd_rate",
  "gas_prd_rate",
  "water_prd_rate",
  "oil",
  "gas",
  "liquid",
]);

// ---------------------------------------------------------------------------
// 2. Per-lever display metadata fallback (unit + nominal equipment limits +
//    constraint hint). Used when the BE lift_template omits a lever's meta.
// ---------------------------------------------------------------------------
export const LEVER_META = {
  choke_size: { name: "Choke", unit: "/64 in", min: 8, max: 128, hint: "Open up to critical lift velocity; watch erosional limit." },
  esp_frequency: { name: "ESP Frequency", unit: "Hz", min: 40, max: 65, hint: "Stay inside frequency window; avoid gas-lock / pump-off & thrust limits." },
  gas_inj_rate: { name: "Gas Inj. Rate", unit: "Mscf/d", min: 0, max: 2000, hint: "Humped response — there is an OPTIMAL rate; more gas past it lowers rate." },
  gas_inj_pressure: { name: "Gas Inj. Pressure", unit: "psi", min: 0, max: 2500, hint: "Must stay below available injection pressure; avoid heading/slugging." },
  water_inj_rate: { name: "Water Inj. Rate", unit: "bbl/d", min: 0, max: 5000, hint: "Limited by water-handling & fracture pressure." },
  co2_inj_rate: { name: "CO2 Inj. Rate", unit: "Mscf/d", min: 0, max: 2000, hint: "Limited by injectivity & fracture pressure." },
  target_thp: { name: "Target THP", unit: "psi", min: 0, max: 3000, hint: "Lower drawdown setpoint → higher rate, but bounded (coning/sand)." },
  wellhead_back_pressure: { name: "WH Back Pressure", unit: "psi", min: 0, max: 3000, hint: "Lower back pressure → higher rate; bounded by separator." },
  spm: { name: "Strokes / min", unit: "SPM", min: 1, max: 12, hint: "Bounded by pump fillage & rod loading; over-pumping causes fluid pound." },
  stroke_length: { name: "Stroke Length", unit: "in", min: 24, max: 168, hint: "Bounded by unit geometry & rod stress." },
  pump_rpm: { name: "Pump Speed", unit: "RPM", min: 50, max: 500, hint: "Bounded by torque & stator temperature; avoid dry-run." },
  cycle_time: { name: "Cycle Time", unit: "min", min: 5, max: 240, hint: "Optimal on/off period; needs minimum reservoir energy." },
  power_fluid_rate: { name: "Power Fluid Rate", unit: "bbl/d", min: 0, max: 4000, hint: "Bounded by cavitation & power-fluid limits." },
};

// ---------------------------------------------------------------------------
// 3. Lift-type → lever template (matrix §5.1). Used when BE omits lift_template.
// ---------------------------------------------------------------------------
export const LIFT_TEMPLATES = {
  natural_flow: {
    label: "Natural Flow",
    optimization_shape: "monotonic", // open up → rate up to critical limit
    levers: ["choke_size", "wellhead_back_pressure"],
    diagnostics: ["Turner critical liquid-loading line", "Nodal analysis"],
  },
  esp: {
    label: "ESP",
    optimization_shape: "windowed", // best inside a frequency window
    levers: ["esp_frequency", "choke_size"],
    diagnostics: ["ESP pump / affinity curve", "Intake pressure"],
  },
  gas_lift: {
    label: "Gas Lift",
    optimization_shape: "humped", // OPTIMAL injection rate, then declines
    levers: ["gas_inj_rate", "gas_inj_pressure"],
    diagnostics: ["Gas-lift performance curve (GLPC)", "Optimal GLR"],
  },
  rod_pump: {
    label: "Rod Pump (SRP)",
    optimization_shape: "fillage_limited",
    levers: ["spm", "stroke_length"],
    diagnostics: ["Dynamometer card", "Pump fillage"],
  },
  pcp: {
    label: "PCP",
    optimization_shape: "torque_limited",
    levers: ["pump_rpm"],
    diagnostics: ["Torque-speed curve", "Stator temperature"],
  },
  plunger: {
    label: "Plunger Lift",
    optimization_shape: "cyclic",
    levers: ["cycle_time"],
    diagnostics: ["Plunger cycle chart"],
  },
  jet_pump: {
    label: "Hydraulic Jet Pump",
    optimization_shape: "cavitation_limited",
    levers: ["power_fluid_rate"],
    diagnostics: ["Pump performance curve"],
  },
};

// Default (unknown lift) — conservative monotonic set, choke only.
export const DEFAULT_LIFT_TEMPLATE = {
  label: "Unspecified Lift",
  optimization_shape: "monotonic",
  levers: ["choke_size", "target_thp"],
  diagnostics: ["Nodal analysis"],
};

// ---------------------------------------------------------------------------
// 4. Normalisers — tolerate the many raw spellings that may arrive from
//    well_summary.artificial_lift_type (and BE template lift_type).
// ---------------------------------------------------------------------------
export function normalizeLiftType(raw) {
  if (!raw) return null;
  const s = String(raw).toLowerCase().replace(/[\s\-_./]+/g, "");
  if (/esp|electricsubmersible|submersible|电潜|潜油/.test(s)) return "esp";
  if (/gaslift|gl|气举/.test(s)) return "gas_lift";
  if (/rod|beam|sucker|srp|pumpingunit|抽油|有杆/.test(s)) return "rod_pump";
  if (/pcp|progressivecavity|screwpump|螺杆/.test(s)) return "pcp";
  if (/plunger|柱塞/.test(s)) return "plunger";
  if (/jet|hydraulic|射流|水力/.test(s)) return "jet_pump";
  if (/natural|flowing|selfflow|自喷|naturalflow/.test(s)) return "natural_flow";
  return null; // unknown → caller uses DEFAULT_LIFT_TEMPLATE
}

// The BE `controllable_levers` payload may arrive either as plain column-key
// strings (["injection_gas", ...]) OR as rich spec objects
// ([{col, key, unit, shape, limit, note}, ...] — what dca_management currently
// emits). Coerce either form to a list of column-key strings.
export function leverKeysOf(controllableLevers) {
  if (!Array.isArray(controllableLevers)) return null;
  return controllableLevers
    .map((l) => (typeof l === "string" ? l : l && (l.col || l.key)))
    .filter(Boolean);
}

export function isControllableLever(paramKey, beLeverKeys) {
  const keys = leverKeysOf(beLeverKeys);
  if (keys && keys.length) {
    return keys.includes(paramKey);
  }
  return CONTROLLABLE_LEVER_KEYS.has(paramKey);
}

export function isStateQuantity(paramKey, beLeverKeys) {
  // Anything explicitly listed as a BE lever is NOT a state quantity.
  const keys = leverKeysOf(beLeverKeys);
  if (keys && keys.includes(paramKey)) return false;
  if (RATE_KEYS.has(paramKey)) return false; // rates are targets, not diagnostics
  return STATE_QUANTITY_KEYS.has(paramKey) || !isControllableLever(paramKey, beLeverKeys);
}

/**
 * Resolve the effective lift template for a well, merging BE contract over the
 * static fallback. Returns:
 *   {
 *     liftType,                 // normalized key or null
 *     label,                    // human label
 *     optimizationShape,        // monotonic | windowed | humped | ...
 *     source,                   // "backend" | "fallback" | "degraded"
 *     leverKeys,                // ordered controllable lever param keys
 *     levers: [{ key, name, unit, min, max, hint }],
 *     diagnostics: [...],
 *   }
 */
export function resolveLiftTemplate({
  artificialLiftType, // raw string from well_summary
  beTemplate, // result.lift_template from BE (optional)
  beLeverKeys, // result.controllable_levers from BE (optional)
} = {}) {
  const beShape = beTemplate && beTemplate.optimization_shape;
  const beLiftRaw =
    (beTemplate && beTemplate.lift_type) || artificialLiftType || null;
  const liftType = normalizeLiftType(beLiftRaw);

  const fallback = (liftType && LIFT_TEMPLATES[liftType]) || DEFAULT_LIFT_TEMPLATE;

  // controllable_levers may be strings or {col,key,unit,shape,limit,note} specs.
  const beLeverKeyList = leverKeysOf(beLeverKeys);

  // Lever key ordering: prefer BE controllable_levers, else BE template levers,
  // else static fallback levers.
  let leverKeys = null;
  if (beLeverKeyList && beLeverKeyList.length) {
    leverKeys = [...beLeverKeyList];
  } else if (beTemplate && Array.isArray(beTemplate.levers) && beTemplate.levers.length) {
    leverKeys = beTemplate.levers.map((l) => (typeof l === "string" ? l : l.key));
  } else {
    leverKeys = [...fallback.levers];
  }

  // Build lever metadata, BE meta overriding the static LEVER_META.
  const beLeverMetaByKey = {};
  if (beTemplate && Array.isArray(beTemplate.levers)) {
    beTemplate.levers.forEach((l) => {
      if (l && typeof l === "object" && l.key) beLeverMetaByKey[l.key] = l;
    });
  }
  // Per-column actual lever specs from controllable_levers carry the real
  // unit/shape/limit for THIS well's columns — key them by column name so the
  // lever rows render the right unit & equipment limits (not the generic
  // template key's). Overrides the template-keyed meta above.
  if (Array.isArray(beLeverKeys)) {
    beLeverKeys.forEach((l) => {
      if (!l || typeof l !== "object") return;
      const k = l.col || l.key;
      if (!k) return;
      const lim = l.limit || {};
      beLeverMetaByKey[k] = {
        name: l.name || LEVER_META[l.key]?.name || k,
        unit: l.unit != null ? l.unit : LEVER_META[l.key]?.unit,
        min: lim.min != null ? lim.min : LEVER_META[l.key]?.min,
        max: lim.max != null ? lim.max : LEVER_META[l.key]?.max,
        hint: l.note || LEVER_META[l.key]?.hint,
      };
    });
  }

  const levers = leverKeys.map((key) => {
    const base = LEVER_META[key] || { name: key, unit: "", min: null, max: null, hint: "" };
    const be = beLeverMetaByKey[key] || {};
    return {
      key,
      name: be.name || base.name,
      unit: be.unit != null ? be.unit : base.unit,
      min: be.min != null ? be.min : base.min,
      max: be.max != null ? be.max : base.max,
      hint: be.hint || base.hint,
    };
  });

  // Source classification for the UI banner.
  let source;
  if (beTemplate || (beLeverKeys && beLeverKeys.length)) {
    source = "backend";
  } else if (liftType) {
    source = "fallback"; // we know the lift type, used static template
  } else {
    source = "degraded"; // no lift type at all → default conservative template
  }

  return {
    liftType,
    label: (beTemplate && beTemplate.label) || fallback.label,
    optimizationShape: beShape || fallback.optimization_shape,
    source,
    leverKeys,
    levers,
    diagnostics:
      (beTemplate && beTemplate.diagnostics) || fallback.diagnostics || [],
  };
}
